import BigNumber from 'bignumber.js';

import { DAY } from 'constants/intervals';

import { formatBalance } from 'helpers/format-number';

export const isAmountWithinLimit = (amount: string, limit: string): boolean =>
    new BigNumber(amount).lte(limit);

export const amountToContractValue = (amount: string, decimals: number = 7) =>
    new BigNumber(amount).times(Math.pow(10, decimals)).toFixed();

export const contractValueToAmount = (value: string, decimals: number = 7) =>
    new BigNumber(value).div(Math.pow(10, decimals)).toFixed(decimals);

const STROOPS_PER_UNIT = 10_000_000n;

// Exact conversion of a decimal amount string (as typed in an amount field) to integer stroops.
// Returns null for anything but a non-negative decimal with at most 7 fractional digits.
export const amountToStroops = (amount: string): bigint | null => {
    const match = /^(?=\.?\d)(\d*)(?:\.(\d{0,7}))?$/u.exec((amount ?? '').trim());

    if (!match) {
        return null;
    }

    const [, whole, fraction = ''] = match;

    return BigInt(whole || '0') * STROOPS_PER_UNIT + BigInt(fraction.padEnd(7, '0'));
};

export const stroopsToAmount = (stroops: bigint): string =>
    contractValueToAmount(stroops.toString(), 7);

export const contractValueToFormattedAmount = (
    value: string | number | bigint,
    decimals = 7,
    withRounding = false,
    withLetters?: boolean,
    decimal = 7,
    absolute = false,
) =>
    formatBalance(
        absolute
            ? Math.abs(Number(contractValueToAmount(String(value ?? 0), decimals)))
            : Number(contractValueToAmount(String(value ?? 0), decimals)),
        withRounding,
        withLetters,
        decimal,
    );

export const apyValueToDisplay = (apy: string) =>
    apy ? `${formatBalance((+apy * 100).toFixed(2))}%` : '0%';

export const tpsToDailyAmount = (tps: string, decimals = 7, withLetters?: boolean) => {
    const seconds = DAY / 1000;
    const value = contractValueToAmount((+tps * seconds).toString(), decimals);

    return formatBalance(value, true, withLetters);
};

export const toBaseUnits = (amount: string | number, decimals = 7): bigint => {
    const scaled = new BigNumber(amount).shiftedBy(decimals);
    if (!scaled.isFinite() || scaled.isNegative()) throw new Error('Invalid token amount');
    return BigInt(scaled.integerValue(BigNumber.ROUND_DOWN).toFixed(0));
};

export const fromBaseUnits = (amount: bigint, decimals = 7): string =>
    new BigNumber(amount.toString()).shiftedBy(-decimals).toFixed();
